import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  PlayCircleOutline as PlayIcon,
  Flag as FlagIcon,
  HelpOutline as HelpIcon,
  Star as StarIcon,
  Warning as WarningIcon,
  Replay as ReplayIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { api } from '../../services/api';
import { useNavigate } from 'react-router-dom';

const getVideoUrl = (path: string | null) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  const baseUrl = process.env.REACT_APP_API_URL || 'https://localhost:7245/api';
  return `${baseUrl}${path.startsWith('/') ? '' : '/'}${path}`;
};

interface Clip {
  id: number;
  title: string;
  videoUrl: string;
  hazardStartSecond: number;
  hazardDurationSeconds: number;
  explanation: string;
}

export const HazardPerception: React.FC = () => {
  const navigate = useNavigate();
  const [clips, setClips] = useState<Clip[]>([]);
  const [selectedClip, setSelectedClip] = useState<Clip | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Test State
  const [isPlaying, setIsPlaying] = useState(false);
  const [clicks, setClicks] = useState<number[]>([]); // Click seconds
  const [testFinished, setTestFinished] = useState(false);
  const [scoreResult, setScoreResult] = useState<any>(null);
  const [scoringLoading, setScoringLoading] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    fetchClips();
  }, []);

  const fetchClips = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.getHazardClips();
      setClips(res.data || []);
    } catch (err: any) {
      console.error('Failed to load hazard clips:', err);
      setError('An error occurred loading hazard test clips.');
    } finally {
      setLoading(false);
    }
  };

  const startClipTest = (clip: Clip) => {
    setSelectedClip(clip);
    setClicks([]);
    setTestFinished(false);
    setScoreResult(null);
    setIsPlaying(true);
  };

  const handleVideoClick = () => {
    if (!videoRef.current || testFinished) return;
    const clickTime = videoRef.current.currentTime;
    
    // Prevent double clicking the exact same millisecond
    if (clicks.includes(clickTime)) return;
    
    setClicks(prev => [...prev, clickTime]);
  };

  const handleVideoEnded = async () => {
    if (!selectedClip) return;
    setTestFinished(true);

    try {
      setScoringLoading(true);
      // In the real DVSA test, multiple clicks are analyzed.
      // We look for the first click that falls into the hazard scoring window.
      // If none, we take the closest click or send the first click.
      // Let's find if any click is in the window, or just send the first click overall.
      let clickTimeToSend = clicks.length > 0 ? clicks[0] : -1;
      
      const windowStart = selectedClip.hazardStartSecond;
      const windowEnd = selectedClip.hazardStartSecond + selectedClip.hazardDurationSeconds;
      
      const clickInWindow = clicks.find(t => t >= windowStart && t <= windowEnd);
      if (clickInWindow !== undefined) {
        clickTimeToSend = clickInWindow;
      } else if (clicks.length > 0) {
        clickTimeToSend = clicks[0]; // send the first click if none in window
      }

      if (clickTimeToSend !== -1) {
        const res = await api.scoreHazardReaction(selectedClip.id, clickTimeToSend);
        setScoreResult(res.data);
      } else {
        setScoreResult({
          score: 0,
          clickSecond: null,
          hazardStart: selectedClip.hazardStartSecond,
          hazardEnd: selectedClip.hazardStartSecond + selectedClip.hazardDurationSeconds,
          explanation: selectedClip.explanation
        });
      }
    } catch (err) {
      console.error('Scoring failed:', err);
      setError('Failed to compute score.');
    } finally {
      setScoringLoading(false);
    }
  };

  const handleBackToClips = () => {
    setSelectedClip(null);
    setClicks([]);
    setTestFinished(false);
    setScoreResult(null);
    setIsPlaying(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Back button */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={selectedClip ? handleBackToClips : () => navigate('/student/dashboard')}
          sx={{ fontWeight: 'bold' }}
        >
          {selectedClip ? 'Back to Practice Clips' : 'Back to Student Dashboard'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {!selectedClip ? (
        <Box>
          <Typography variant="h4" fontWeight="bold" color="primary" gutterBottom>
            DVSA Hazard Perception Practice
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            In the official UK driving theory test, you must watch 14 video clips containing road hazards. Click as soon as you see a developing hazard starting to occur. Early clicks receive up to 5 points.
          </Typography>

          <Grid container spacing={4}>
            {/* Left: Practice Clips Grid */}
            <Grid size={{ xs: 12, md: 8 }}>
              <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
                Available Practice Video Clips
              </Typography>
              <Grid container spacing={3}>
                {clips.map((clip) => (
                  <Grid size={{ xs: 12, sm: 6 }} key={clip.id}>
                    <Card sx={{ borderRadius: 3, boxShadow: '0 4px 15px rgba(0,0,0,0.03)', border: '1px solid #eef2f6', height: '100%', display: 'flex', flexDirection: 'column' }}>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', justifyItems: 'space-between' }}>
                        <Typography variant="subtitle1" fontWeight="bold" color="text.primary" gutterBottom>
                          {clip.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3, flexGrow: 1 }}>
                          Click reaction test clip modeled on real DVSA motorway, wet city, and rural road hazards.
                        </Typography>
                        <Button
                          variant="contained"
                          color="primary"
                          startIcon={<PlayIcon />}
                          onClick={() => startClipTest(clip)}
                          fullWidth
                          sx={{ borderRadius: 2, fontWeight: 'bold' }}
                        >
                          Start Test
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* Right: Instructions Info Panel */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Card sx={{ borderRadius: 3, border: '1px solid #e0e0e0', boxShadow: 'none', bgcolor: 'rgba(25,118,210,0.01)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HelpIcon /> How to Pass the Test
                  </Typography>
                  <List dense>
                    <ListItem sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}><StarIcon color="warning" fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Developing Hazard" secondary="Look for anything that forces you to change speed or direction (e.g. car joining road, pedestrian stepping off curb)." />
                    </ListItem>
                    <ListItem sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}><StarIcon color="warning" fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Scoring Window" secondary="The scoring window opens the split-second the hazard starts. Clicking early gets 5 points. Late clicks yield 4, 3, 2, or 1 point." />
                    </ListItem>
                    <ListItem sx={{ alignItems: 'flex-start' }}>
                      <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}><WarningIcon color="error" fontSize="small" /></ListItemIcon>
                      <ListItemText primary="Do Not Cheat" secondary="Clicking repeatedly or in a pattern triggers a cheat safeguard resulting in a score of 0. Click only when a hazard develops." />
                    </ListItem>
                  </List>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      ) : (
        /* Video Test Scene */
        <Box display="flex" flexDirection="column" alignItems="center">
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
            {selectedClip.title}
          </Typography>

          <Paper
            elevation={4}
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: 854,
              aspectRatio: '16/9',
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: '#000',
              cursor: 'crosshair',
              mb: 3
            }}
            onClick={handleVideoClick}
          >
            <video
              ref={videoRef}
              src={getVideoUrl(selectedClip.videoUrl)}
              onEnded={handleVideoEnded}
              autoPlay
              muted
              playsInline
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            
            {/* Click Flag Overlay - matches official DVSA test flag UI */}
            {clicks.length > 0 && (
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 12,
                  left: 12,
                  bgcolor: 'rgba(255, 255, 255, 0.85)',
                  px: 1.5,
                  py: 0.5,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 2px 10px rgba(0,0,0,0.1)'
                }}
              >
                <FlagIcon color="error" fontSize="small" />
                <Typography variant="caption" fontWeight="bold" color="text.primary">
                  Click Registered ({clicks.length})
                </Typography>
              </Box>
            )}
          </Paper>

          <Typography variant="body2" color="text.secondary">
            Click or tap inside the video box as soon as you spot a developing hazard.
          </Typography>
        </Box>
      )}

      {/* Scoring Dialog */}
      <Dialog open={testFinished && scoreResult !== null} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 'bold', color: '#1a237e', textAlign: 'center', pt: 3 }}>
          Hazard Test Completed
        </DialogTitle>
        <DialogContent dividers sx={{ py: 3 }}>
          {scoringLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            scoreResult && (
              <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                <Box display="flex" alignItems="center" gap={0.5}>
                  {[1, 2, 3, 4, 5].map((s) => (
                    <StarIcon
                      key={s}
                      sx={{
                        fontSize: 48,
                        color: s <= scoreResult.score ? '#ffb300' : '#e0e0e0',
                        filter: s <= scoreResult.score ? 'drop-shadow(0 2px 4px rgba(255,179,0,0.2))' : 'none'
                      }}
                    />
                  ))}
                </Box>

                <Typography variant="h5" fontWeight="black" color={scoreResult.score > 0 ? 'success.main' : 'error.main'}>
                  Score: {scoreResult.score} / 5
                </Typography>

                <Paper variant="outlined" sx={{ p: 2.5, width: '100%', bgcolor: '#fafafa', borderRadius: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold" color="text.primary" gutterBottom>
                    DVSA Explanatory Guidance
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {scoreResult.explanation}
                  </Typography>
                </Paper>

                {scoreResult.clickSecond !== null ? (
                  <Typography variant="caption" color="text.secondary">
                    Hazard window opened at: <strong>{scoreResult.hazardStart.toFixed(1)}s</strong>. You clicked at: <strong>{scoreResult.clickSecond.toFixed(1)}s</strong>.
                  </Typography>
                ) : (
                  <Typography variant="caption" color="text.secondary">
                    No clicks were registered during the active hazard window.
                  </Typography>
                )}
              </Box>
            )
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2.5, justifyContent: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleBackToClips}
            sx={{ fontWeight: 'bold', px: 3 }}
          >
            Choose Another Clip
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<ReplayIcon />}
            onClick={() => selectedClip && startClipTest(selectedClip)}
            sx={{ fontWeight: 'bold', px: 3 }}
          >
            Try Again
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default HazardPerception;
